import { useEffect, useMemo, useState } from 'react';
import { Button, Collapse, Empty, Input } from 'antd';
import {
  CalendarCheck,
  Coins,
  Fire,
  Gift,
  MagnifyingGlass,
  Phone,
  SignIn,
  UserCircle,
  type Icon,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { canAccessAdminWeb, useSession } from '../auth/session';
import { supportArticles, supportContact, supportTopics } from '../features/support/content';
import { filterArticles } from '../features/support/search';
import { tokens } from '../theme';

const topicIcons: Record<string, Icon> = {
  'dang-nhap': SignIn,
  'diem-hang': Coins,
  'qua-voucher': Gift,
  'dat-ban': CalendarCheck,
  'tai-khoan': UserCircle,
  'ho-tro': Phone,
};

const topicTitle = new Map(supportTopics.map((topic) => [topic.id, topic.title]));

function scrollToArticles(): void {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.getElementById('bai-huong-dan')?.scrollIntoView({
    behavior: reduceMotion ? 'auto' : 'smooth',
    block: 'start',
  });
}

export function SupportPage() {
  const navigate = useNavigate();
  const session = useSession((state) => state.session);
  const signedIn = canAccessAdminWeb(session);

  const [query, setQuery] = useState('');
  const [topicId, setTopicId] = useState<string | null>(null);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const visible = useMemo(
    () => filterArticles(supportArticles, query, topicId),
    [query, topicId],
  );

  useEffect(() => {
    const previous = document.title;
    document.title = 'Trung tâm hỗ trợ';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    if (query.trim().length > 0) {
      setOpenKeys(visible.map((article) => article.id));
    }
  }, [query, visible]);

  const selectTopic = (id: string): void => {
    setTopicId((current) => (current === id ? null : id));
    scrollToArticles();
  };

  return (
    <div style={{ minHeight: '100vh', background: tokens.color.canvas }}>
      <style>{`
        .support-articles .ant-collapse-header {
          align-items: center !important;
          min-height: 44px;
        }
        @media (prefers-reduced-motion: reduce) {
          .support-articles .ant-collapse-item,
          .support-articles .ant-collapse-content {
            transition: none !important;
          }
        }
      `}</style>

      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          minHeight: tokens.layout.topbarHeight,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 20px',
          background: tokens.color.surface,
          borderBottom: `1px solid ${tokens.color.line}`,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 36,
            height: 36,
            flex: 'none',
            borderRadius: tokens.radius.control,
            background: tokens.color.green,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
          }}
        >
          <Fire size={20} weight="fill" />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>Hỗ trợ thành viên</div>
          <div style={{ fontSize: 11, color: tokens.color.ink3 }}>Trung tâm hỗ trợ</div>
        </div>
        {signedIn && (
          <Button
            type="primary"
            size="large"
            style={{ marginLeft: 'auto' }}
            onClick={() => navigate('/')}
          >
            Vào bảng điều khiển
          </Button>
        )}
      </header>

      <main
        style={{
          maxWidth: 960,
          margin: '0 auto',
          padding: `${tokens.space.sectionGap}px 20px 48px`,
        }}
      >
        <h1
          style={{
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            margin: '8px 0 8px',
          }}
        >
          Trung tâm hỗ trợ
        </h1>
        <p style={{ margin: '0 0 20px', maxWidth: 640, fontSize: 13, lineHeight: 1.55, color: tokens.color.ink2 }}>
          Hướng dẫn dùng app thành viên. Dưới cùng màn hình có năm tab: Trang chủ, Chi nhánh, Quét mã,
          Ưu đãi, Cá nhân.
        </p>

        <label
          htmlFor="support-search"
          style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}
        >
          Tìm hướng dẫn
        </label>
        <Input
          id="support-search"
          size="large"
          allowClear
          value={query}
          placeholder="Từ khóa, ví dụ quét hóa đơn"
          prefix={<MagnifyingGlass size={16} aria-hidden />}
          onChange={(event) => setQuery(event.target.value)}
          style={{ maxWidth: 480, marginBottom: tokens.space.sectionGap }}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 12,
            marginBottom: tokens.space.sectionGap,
          }}
        >
          {supportTopics.map((topic) => {
            const Icon = topicIcons[topic.id] ?? SignIn;
            const selected = topicId === topic.id;
            return (
              <button
                key={topic.id}
                type="button"
                aria-pressed={selected}
                onClick={() => selectTopic(topic.id)}
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  minHeight: 44,
                  padding: tokens.space.cardPadding,
                  borderRadius: tokens.radius.card,
                  border: `1px solid ${selected ? tokens.color.green : tokens.color.line}`,
                  background: selected ? tokens.color.green50 : tokens.color.surface,
                  boxShadow: tokens.shadow.card,
                  color: tokens.color.ink,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Icon size={18} aria-hidden color={tokens.color.green} />
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{topic.title}</span>
                  {selected && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: tokens.color.green600 }}>
                      Đang xem
                    </span>
                  )}
                </span>
                <span style={{ display: 'block', fontSize: 12, lineHeight: 1.45, color: tokens.color.ink2 }}>
                  {topic.description}
                </span>
              </button>
            );
          })}
        </div>

        <section id="bai-huong-dan" aria-live="polite" style={{ scrollMarginTop: 80 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
              {topicId === null ? 'Tất cả bài' : topicTitle.get(topicId)}
            </h2>
            <span style={{ fontSize: 12, color: tokens.color.ink3 }}>
              {query.trim().length === 0 && topicId === null
                ? `${supportArticles.length} bài`
                : `${visible.length} bài khớp`}
            </span>
          </div>

          {visible.length === 0 ? (
            <div
              style={{
                background: tokens.color.surface,
                border: `1px solid ${tokens.color.line}`,
                borderRadius: tokens.radius.card,
                padding: tokens.space.sectionGap,
              }}
            >
              <Empty description="Không có bài nào khớp. Thử từ khóa khác hoặc bỏ lọc chủ đề." />
            </div>
          ) : (
            <Collapse
              className="support-articles"
              activeKey={openKeys}
              onChange={(key) => setOpenKeys(Array.isArray(key) ? key.map(String) : [String(key)])}
              style={{ background: tokens.color.surface, borderRadius: tokens.radius.card }}
              items={visible.map((article) => ({
                key: article.id,
                label: (
                  <span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        color: tokens.color.orange600,
                      }}
                    >
                      {topicTitle.get(article.topicId)}
                    </span>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{article.title}</span>
                    <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: tokens.color.ink2 }}>
                      {article.summary}
                    </span>
                  </span>
                ),
                children: (
                  <ol style={{ margin: 0, paddingLeft: 20, color: tokens.color.ink, fontSize: 13, lineHeight: 1.55 }}>
                    {article.steps.map((step) => (
                      <li key={step} style={{ marginBottom: 6 }}>
                        {step}
                      </li>
                    ))}
                  </ol>
                ),
              }))}
            />
          )}
        </section>

        <section
          style={{
            marginTop: tokens.space.sectionGap,
            padding: tokens.space.sectionGap,
            background: tokens.color.surface,
            border: `1px solid ${tokens.color.line}`,
            borderRadius: tokens.radius.container,
            boxShadow: tokens.shadow.card,
          }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px' }}>{supportContact.title}</h2>
          {supportContact.paragraphs.map((paragraph) => (
            <p key={paragraph} style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.55, color: tokens.color.ink2 }}>
              {paragraph}
            </p>
          ))}
        </section>
      </main>
    </div>
  );
}
